import React, { useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import axiosPrivate from 'config/axiosPrivate';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import { clientOrderDisplaySelector } from 'store/Client/OrderDisplay';
import { UserSessionSelector } from 'store/UserSession';

import { clientUpdateTextsOfTextList } from 'store/Client/OrderDisplay';

import {
  setClientOrderId,
  setTextType as clientSetTextType,
  setWriterCommandId,
} from 'store/Client/ClientAnalyser';
import { fetchClientText } from 'store/Client/ClientAnalyser/features';

import ErrorComponent from 'components/shared/ErrorComponent';
import { Loader } from 'components/shared/Loader';
import PageNbOptions from 'components/shared/PageNbOptions';
import SearchComponent from 'components/shared/SearchBar/SearchComponent';
import ErrorSuccesModal from 'components/shared/modal/ErrorSuccesModal';
import ConfirmationModal from 'components/shared/modal/ConfirmationModal';

import GlobalRevisionRequestModal from 'components/shared/modal/GlobalRevisionRequestModal';
import DownloadModal from 'components/shared/modal/DownloadModal';
import CustomFilterMenu from 'components/shared/Filter/CustomFilterMenu';
import OrderTextProgress from 'components/shared/OrderDetails/OrderTextProgress';

import { ValidationAndRevisionCheckBoxGroup } from './ValidationAndRevisionCheckBoxGroup';
import { DownloadButton } from './DownLoadButton';
import { TextList } from './TextList';

import { useTextManagement } from 'utils/hooks/useTextManagement';
import { useTextFilter } from 'utils/hooks/useTextFilter';
import { useHistoryState } from 'utils/hooks/useHistoryState';
import { PAGENUMBERSTINYLIMIT } from 'utils/Constants';
import { filterTextByStatus, filterTextByType } from 'utils/Constants';

import './Style.css';

export const ClientOrderTextsDetailsList = (props) => {
  const { user } = useSelector(UserSessionSelector);
  const { t } = useTranslation();

  const { order, textList, isTextLoading, isTextError } = useSelector(
    clientOrderDisplaySelector
  );

  const dispatch = useDispatch();
  const history = useHistory();

  const [selectedStatus, setSelectedStatus] = useHistoryState(
    'selectedFilter',
    filterTextByStatus[0]
  );
  const [selectedType, setSelectedType] = useState(filterTextByType[0]);

  const [searchValue, setSearchValue] = useState('');

  const [currentPage, setCurrentPage] = useState(1);

  const [textsPerPage, setTextsPerPage] = useState(10);

  const [selectedTextsForRevision, setSelectedTextsForRevision] = useState([]);
  const [selectedTextsForValidation, setSelectedTextsForValidation] = useState(
    []
  );

  const [requestText, setRequestText] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [showRevisionModal, setShowRevisionModal] = useState(false);

  const [confirmationMsg, setConfirmationMsg] = useState('');
  const [confirmation, setConfirmation] = useState(false);

  const [isTranslate, setIsTranslate] = useState(false);
  const [isText, setIsText] = useState(false);
  const [isCombo, setIsCombo] = useState(false);

  const [errorSuccess, setErrorSuccess] = useState(false);
  const [errorSuccessMsg, setErrorSuccessMsg] = useState('');
  const [isSuccess, setIsSuccess] = useState(false);
  const [isInfo, setIsInfo] = useState(false);
  const [errorSuccessImg, setErrorSuccessImg] = useState(undefined);

  const [showDownloadModal, setShowDownloadModel] = useState(false);

  const { currentTexts, sortedTexts, pageNumbers } = useTextManagement(
    user.role,
    textList,
    selectedStatus,
    selectedType,
    searchValue,
    textsPerPage,
    currentPage
  );

  const {
    isTextEligibleForValidation,
    availbleRevisionNumber,
    availableValidationNumber,
    isTextEligibleForRevision,
    isTranslateOnly,
    isTextOnly,
    isCombined,
  } = useTextFilter(sortedTexts);

  const filterTextByStatusArr = filterTextByStatus;

  const [anchorEl, setAnchorEl] = React.useState(null);
  const open = Boolean(anchorEl);

  const eligibleTextForValidation = useMemo(() => {
    return sortedTexts
      .filter((text) => isTextEligibleForValidation(text))
      .map((text) => text.id);
  }, [sortedTexts]);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleSearchValueChange = (newValue) => {
    setSearchValue(newValue);
  };

  const getPath = (orderId, textId, writerCommandId, texteType) => {
    dispatch(setClientOrderId(orderId));
    dispatch(fetchClientText(textId));
    dispatch(setWriterCommandId(writerCommandId));
    dispatch(clientSetTextType(texteType));
    history.push('/Client/TextDetails');
  };

  const canRequestGlobalRevision = () => {
    return (
      order.type !== 'SMART' &&
      availbleRevisionNumber > 1 &&
      !order.revisionDisabled
    );
  };

  const canValidateMultipleTexts = () => availableValidationNumber > 0;

  const handleClientTextValidation = (textId) => {
    setSelectedTextsForValidation((prev) =>
      prev.includes(textId)
        ? prev.filter((id) => id !== textId)
        : [...prev, textId]
    );
  };

  const handleGlobalValidation = () => {
    if (selectedTextsForValidation.length > 0) {
      setConfirmationMsg(`Voulez-vous vraiment valider les textes cochés ?`);
    } else {
      setSelectedTextsForValidation(eligibleTextForValidation);
      setConfirmationMsg(`Voulez-vous vraiment valider tous les textes ?`);
    }
    setConfirmation(true);
  };

  const confirmValidation = async () => {
    try {
      const isAllTextsSelected =
        selectedTextsForValidation.length === props.orderTextAmount;

      if (isAllTextsSelected) {
        await axiosPrivate.put(`/commands/${order.id}/validateAllTexts`);
      } else {
        await axiosPrivate.post(
          '/texts/multi-validate',
          selectedTextsForValidation
        );
      }

      dispatch(
        clientUpdateTextsOfTextList({
          selectedTexts: selectedTextsForValidation,
          key: 'validated',
          value: true,
        })
      );
      setSelectedTextsForValidation([]);

      setIsSuccess(true);
      setErrorSuccessMsg(
        t(
          selectedTextsForValidation.length > 1
            ? 'Les textes ont été validés avec succès.'
            : 'Le texte a été validé avec succès.'
        )
      );
      setErrorSuccess(true);
    } catch (error) {
      console.error('Error validating texts:', error);
    } finally {
      setConfirmation(false);
    }
  };

  if (isTextLoading) return <Loader />;

  if (isTextError) return <ErrorComponent />;

  return (
    <div>
      <div className="box-wrapper" style={{ height: '480px' }}>
        <OrderTextProgress
          orderTextAmount={props.orderTextAmount}
          textList={textList}
          t={t}
        />
        <div className="toolbar-container">
          <div className="toolbar-left">
            <ValidationAndRevisionCheckBoxGroup
              userRole={user.role}
              availableValidationNumber={availableValidationNumber}
              availableRevisionNumber={availbleRevisionNumber}
              eligibleTextForValidation={eligibleTextForValidation}
              isTextEligibleForValidation={isTextEligibleForValidation}
              orderType={order.type}
              revisionDisabled={order.revisionDisabled}
              selectedTextsForValidation={selectedTextsForValidation}
              selectedTextsForRevision={selectedTextsForRevision}
              sortedTexts={sortedTexts}
              setSelectedTextsForRevision={setSelectedTextsForRevision}
              setSelectedTextsForValidation={setSelectedTextsForValidation}
            />
          </div>

          <div className="toolbar-center">
            <SearchComponent
              placeholder={t('Rechercher')}
              searchValue={searchValue}
              onSearchValueChange={handleSearchValueChange}
            />
          </div>

          <div className="toolbar-right">
            <CustomFilterMenu
              selectedStatus={selectedStatus}
              handleClick={handleClick}
              anchorEl={anchorEl}
              open={open}
              handleClose={handleClose}
              isTranslateOnly={isTranslateOnly}
              isTextOnly={isTextOnly}
              isCombined={isCombined}
              t={t}
              isText={isText}
              isTranslate={isTranslate}
              isCombo={isCombo}
              setIsTranslate={setIsTranslate}
              setIsText={setIsText}
              setIsCombo={setIsCombo}
              setSelectedStatus={setSelectedStatus}
              filterTextByStatusArr={filterTextByStatusArr}
              filterTextByType={filterTextByType}
              setSelectedType={setSelectedType}
              flag={false}
            />
            <DownloadButton
              sortedTexts={sortedTexts}
              user={user}
              setShowDownloadModel={setShowDownloadModel}
            />
          </div>
        </div>

        <TextList
          currentTexts={currentTexts}
          currentPage={currentPage}
          textsPerPage={textsPerPage}
          user={user}
          order={order}
          selectedTextsForValidation={selectedTextsForValidation}
          handleClientTextValidation={handleClientTextValidation}
          selectedTextsForRevision={selectedTextsForRevision}
          setSelectedTextsForRevision={setSelectedTextsForRevision}
          setSelectedTextsForValidation={setSelectedTextsForValidation}
          isTextEligibleForValidation={isTextEligibleForValidation}
          isTextEligibleForRevision={isTextEligibleForRevision}
          availableRevisionNumber={availbleRevisionNumber}
          getPath={getPath}
          setErrorSuccessMsg={setErrorSuccessMsg}
          setErrorSuccessImg={setErrorSuccessImg}
          setIsSuccess={setIsSuccess}
          setIsInfo={setIsInfo}
          setErrorSuccess={setErrorSuccess}
          t={t}
        />
      </div>

      <div className="d-flex justify-content-end">
        {canValidateMultipleTexts() && (
          <button
            className={
              selectedTextsForValidation.length < 1 ||
              availableValidationNumber < 1
                ? 'bt-submit-disabled-y me-2'
                : 'bt-submit-y me-2'
            }
            type="submit"
            onClick={handleGlobalValidation}
            disabled={
              selectedTextsForValidation.length < 1 ||
              availableValidationNumber < 1
            }
          >
            {t('Valider les textes')}
          </button>
        )}
        {canRequestGlobalRevision() && (
          <button
            className={
              selectedTextsForRevision.length < 2
                ? 'bt-submit-disabled-y'
                : 'bt-submit-y'
            }
            type="submit"
            onClick={() => setShowRevisionModal(true)}
            disabled={selectedTextsForRevision.length < 2}
          >
            {t('Repasse globale')}
          </button>
        )}

        <div className="d-flex box-pagination">
          <div className="list-page-number me-3 ">
            {pageNumbers
              .slice(
                Math.max(0, currentPage - PAGENUMBERSTINYLIMIT),
                Math.min(currentPage + PAGENUMBERSTINYLIMIT, pageNumbers.length)
              )
              .map((pageNum, index) => (
                <span
                  key={index}
                  className={pageNum === currentPage ? 'active' : ''}
                  onClick={() => setCurrentPage(pageNum)}
                >
                  {pageNum}
                </span>
              ))}
          </div>
          <PageNbOptions
            elementsPerPage={textsPerPage}
            setElementsPerPage={setTextsPerPage}
          />
        </div>
      </div>

      <GlobalRevisionRequestModal
        textIds={selectedTextsForRevision}
        setTextIds={setSelectedTextsForRevision}
        showRevisionModal={showRevisionModal}
        requestText={requestText}
        selectedFile={selectedFile}
        handleClose={() => {
          setShowRevisionModal(false);
          setRequestText(null);
          setSelectedFile(null);
          setSelectedTextsForRevision([]);
        }}
        setRequestText={(value) => setRequestText(value)}
        setSelectedFile={(value) => setSelectedFile(value)}
        orderId={order.id}
      />
      <ErrorSuccesModal
        open={errorSuccess}
        message={errorSuccessMsg}
        imageUrl={errorSuccessImg}
        handleClose={() => setErrorSuccess(false)}
        success={isSuccess}
        info={isInfo}
        toggle={() => setErrorSuccess(!errorSuccess)}
      />
      <ConfirmationModal
        open={confirmation}
        message={confirmationMsg}
        handleClose={() => {
          setConfirmation(false);
          setSelectedTextsForValidation([]);
        }}
        executeAction={confirmValidation}
      />
      <DownloadModal
        open={showDownloadModal}
        handleClose={() => setShowDownloadModel(false)}
      />
    </div>
  );
};
